import {env} from '@/services/env/client'

/**
 * The production hostname for this tower, extracted from NEXT_PUBLIC_APP_URL.
 * Used only in production to avoid converting links to other towers.
 */
const APP_HOSTNAME = (() => {
	try {
		return new URL(env.NEXT_PUBLIC_APP_URL).hostname.toLowerCase()
	} catch {
		return ''
	}
})()

/**
 * True only when the app is deployed to a real production domain (*.elanco.com).
 * NODE_ENV is always 'production' in any Next.js build, so we can't rely on it
 * to distinguish prod from staging/changeset deployments.
 */
const IS_PRODUCTION =
	APP_HOSTNAME.endsWith('.elanco.com') || APP_HOSTNAME === 'elanco.com'

/**
 * Converts an absolute URL to a relative path for internal Elanco domains.
 *
 * In non-production environments (local, staging, changeset), ALL internal
 * Elanco URLs are converted so navigation stays on the current origin.
 *
 * In production, only URLs matching this tower's hostname (NEXT_PUBLIC_APP_URL)
 * are converted — other Elanco/elancoapps domains stay absolute so the browser
 * navigates to the correct external site.
 */
export function toRelativeUrl(url: string): string {
	if (!url.toLowerCase().startsWith('http')) {
		return url
	}

	try {
		const parsed = new URL(url)
		const hostname = parsed.hostname.toLowerCase()

		const isInternal =
			hostname.endsWith('.elanco.com') ||
			hostname.endsWith('.elancoapps.com') ||
			hostname === 'elanco.com' ||
			hostname === 'elancoapps.com'

		if (!isInternal) {
			return url
		}

		// In non-production, strip all internal Elanco URLs to relative
		if (!IS_PRODUCTION) {
			return parsed.pathname + parsed.search + parsed.hash
		}

		// In production, only strip if the URL is for this tower's hostname
		const isSameTower = APP_HOSTNAME && hostname === APP_HOSTNAME

		if (isSameTower) {
			return parsed.pathname + parsed.search + parsed.hash
		}
	} catch {
		return url
	}

	return url
}
